"use client";

import React, { useEffect, useRef, useState } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Link } from "@/components/common/Link";
import { Music, Play, Disc, Radio } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const projects = [{
  id: 1,
  title: "Moonlit Drift",
  artist: "Lumina Sky",
  category: "MEDITATION",
  releaseDate: "Released: May 26, 2026",
  image: "https://guiding-lionfish.10web.cloud/wp-content/uploads/2026/05/Moonlit-Drift-Album.png?v=sx2wbisngne",
  streamingLinks: {
    spotify: "https://open.spotify.com/album/2ePZWkupGlRcqnzaorIByc?si=KEzoBBTWRlyWZqb9ps92qw",
    apple_music: "https://apple.com",
    amazon_music: "https://music.amazon.com/albums/B0H2VT878D?marketplaceId=ATVPDKIKX0DER&musicTerritory=US&ref=dm_sh_DTf7RWeoldPV4exQvREF0pahW",
    soundcloud: "https://on.soundcloud.com/gzNvGNu3d2MfGmaGU3"
  }
}, {
  id: 2,
  title: "Luminescence",
  artist: "DJ Madame Velvet",
  category: "EDM",
  releaseDate: "Releasing June 12th 2026",
  image: "https://guiding-lionfish.10web.cloud/wp-content/uploads/2026/05/Luminence-Album.png?v=zq1a9j4pn9e",
  streamingLinks: {
    spotify: "https://spotify.com",
    apple_music: "https://apple.com",
    amazon_music: "https://amazon.com/music",
    soundcloud: "https://soundcloud.com"
  }
}];
function ProjectCard({
  project,
  index
}: {
  project: typeof projects[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`flex flex-col gap-4 transition-all duration-700 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}>
      <div className="overflow-hidden rounded-lg shadow-lg border border-white/10 backdrop-blur-md bg-white/5 group relative">
        <img src={project.image} alt={project.title} data-wvc-srcset={project.image.includes('http') ? undefined : `${project.image}-wvc-srcset`} data-wvc-sizes={project.image.includes('http') ? undefined : `${project.image}-wvc-sizes`} loading="lazy" className={`aspect-[4/3] w-full transition-transform duration-500 group-hover:scale-110 ${index === 1 ? "object-contain bg-black/20" : "object-cover"}`} />
        
        {index === 0 && <div className="absolute inset-0 z-10 flex items-center justify-center bg-accent/60 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <div className="flex items-center gap-6 text-white">
              <Link to={project.streamingLinks.spotify} newTab className="hover:scale-110 transition-transform">
                <Music className="size-8 animate-pulse" />
              </Link>
              <Link to={project.streamingLinks.amazon_music} newTab className="hover:scale-110 transition-transform">
                <Disc className="size-8 animate-pulse delay-150" />
              </Link>
              <Link to={project.streamingLinks.soundcloud} newTab className="hover:scale-110 transition-transform">
                <Radio className="size-8 animate-pulse delay-300" />
              </Link>
            </div>
          </div>}
      </div>
      <div className="flex flex-col items-start gap-1">
        <span className="text-xs font-default font-medium uppercase tracking-widest text-accent">
          {project.category}
        </span>
        <h3 className="font-default text-xl font-bold text-foreground tracking-tight">
          {project.title}
        </h3>
        <p className="text-sm font-medium text-muted-foreground">
          {project.artist}
        </p>
        {project.releaseDate && <p className="text-xs text-muted-foreground/60">
            {project.releaseDate}
          </p>}
      </div>
    </div>;
}
export default function Albumslideshowcarousel() {
  return <section data-section-id="229055" id="albumslideshowcarousel" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <Carousel opts={{
        align: "start",
        loop: true
      }} className="w-full">
          <div className="mb-8 flex items-end justify-between gap-4 md:mb-12">
            <div>
              <h2 className="font-default text-3xl font-bold tracking-tighter text-foreground md:text-5xl bg-gradient-to-r from-accent to-secondary bg-clip-text text-transparent">
                New Releases
              </h2>
            </div>
            <div className="flex shrink-0 items-center gap-3">
              <CarouselPrevious variant="outline" className="static size-12 translate-x-0 translate-y-0 rounded-lg border border-accent/30 bg-background/50 text-accent hover:bg-accent hover:text-accent-foreground transition-all shadow-md" />
              <CarouselNext variant="outline" className="static size-12 translate-x-0 translate-y-0 rounded-lg border border-accent/30 bg-background/50 text-accent hover:bg-accent hover:text-accent-foreground transition-all shadow-md" />
            </div>
          </div>

          <CarouselContent className="-ml-4 md:-ml-6">
            {projects.map((project, index) => <CarouselItem key={project.id} className="pl-4 sm:basis-1/2 md:pl-6 lg:basis-1/3 xl:basis-1/4">
                <ProjectCard project={project} index={index} />
              </CarouselItem>)}
          </CarouselContent>
        </Carousel>
      </div>
    </section>;
}